import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FiToggleSwitchLabelAlign,
    FiToggleSwitchSliderColorClass,
} from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';
import { IPlandaySettings } from '@fi-app/external-platform-integration/planday/planday';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { AppTextService } from '@fi-shared/app-text';
import { PlandayService } from '@fi-app/external-platform-integration/planday/planday.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ILightWeightArea, ILightWeightObject } from '@fi-shared/models';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';

@Component({
    selector: 'planday-settings-form',
    templateUrl: './planday-settings-form.component.html',
    styleUrls: ['./planday-settings-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'planday-settings-form' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PlandaySettingsFormComponent,
        },
    ],
    standalone: false,
})
export class PlandaySettingsFormComponent
    extends ControlValueAccessorValidatorBase<IPlandaySettings>
    implements OnInit
{
    @Output() onSavePlandaySettings = new EventEmitter<void>();
    @Input() areas: ILightWeightArea[];
    @Input() businessAreas: ILightWeightObject[];
    @Input() customerDisplayName: string;

    private _selectedAreas$ = new BehaviorSubject<ILightWeightArea[]>([]);

    get selectedAreas$(): Observable<ILightWeightArea[]> {
        return this._selectedAreas$.asObservable();
    }
    get plandaySettings(): IPlandaySettings {
        return this.ngModel;
    }

    @ViewChild(FiValidatingFormDirective, { static: true }) plandaySettingsForm: FiValidatingFormDirective;

    constructor(
        private injector: Injector,
        private appTextService: AppTextService,
        private plandayService: PlandayService,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.updateSelectedAreas();
    }

    private updateSelectedAreas() {
        const selectedAreas = this.areas.filter((area) => this.plandaySettings.AreaIds.includes(area.Id));
        this._selectedAreas$.next(selectedAreas);
    }

    onSettingsChanged() {
        this.plandaySettingsForm.updateValidity();
    }

    onSelectedAreasChanged() {
        this.onSettingsChanged();
        if (!this.plandaySettings.AreaIds.length) {
            this.plandaySettings.IsAutoImportEnabled = false;
            this.plandaySettings.DefaultAreaId = undefined;
            this.onAutoImportEnabledChanged();
        }
        this.updateSelectedAreas();
    }

    onAutoImportEnabledChanged() {
        this.onSettingsChanged();
        if (!this.plandaySettings.IsAutoImportEnabled) {
            this.plandaySettings.SendEmailOnError = this.plandaySettings.CreateFeedItemOnError = false;
        } else {
            this.plandaySettings.SendEmailOnError = true;
        }
    }

    getToggleValueLabel(): string {
        return this.appTextService.get(this.plandaySettings.IsAutoImportEnabled ? 'on' : 'off');
    }

    canSavePlandaySettings(): boolean {
        return !this.plandaySettingsForm.errors;
    }

    getApiResponse() {
        this.plandayService.getApiResponse();
    }

    savePlandaySettings() {
        this.onSavePlandaySettings.emit();
    }

    protected readonly FiToggleSwitchLabelAlign = FiToggleSwitchLabelAlign;
    protected readonly FiToggleSwitchSliderColorClass = FiToggleSwitchSliderColorClass;
}
